import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { QRTemplatesService } from '../../../core/services/qr-templates.service';
import * as QRTemplatesActions from './qr-templates.actions';
import * as QRTemplatesSelectors from './qr-templates.selectors';
import {
  DEFAULT_QR_TEMPLATES_CURRENT_PAGE,
  DEFAULT_QR_TEMPLATES_PER_PAGE,
} from '../models/qr-template.model';

@Injectable()
export class QRTemplatesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private qrTemplatesService = inject(QRTemplatesService);

  loadQRTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.loadQRTemplates),
      switchMap((action) =>
        this.qrTemplatesService
          .getQRTemplatesList({
            page: action.page ?? DEFAULT_QR_TEMPLATES_CURRENT_PAGE,
            per_page: action.perPage ?? DEFAULT_QR_TEMPLATES_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sort_direction: action.sortDirection ?? 'desc',
          })
          .pipe(
            map((response) =>
              QRTemplatesActions.loadQRTemplatesSuccess({
                qrTemplates: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(QRTemplatesActions.loadQRTemplatesFailure({
                error: error?.message ?? 'Failed to load QR templates',
              }))
            )
          )
      )
    )
  );

  loadQRTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.loadQRTemplate),
      switchMap((action) =>
        this.qrTemplatesService.getQRTemplateById(action.uuid).pipe(
          map((template) => QRTemplatesActions.loadQRTemplateSuccess({ template })),
          catchError((error) =>
            of(QRTemplatesActions.loadQRTemplateFailure({
              error: error?.message ?? 'Failed to load QR template',
            }))
          )
        )
      )
    )
  );

  createQRTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.createQRTemplate),
      switchMap((action) =>
        this.qrTemplatesService.createQRTemplate(action.payload).pipe(
          map((template) => QRTemplatesActions.createQRTemplateSuccess({ template })),
          catchError((error) =>
            of(QRTemplatesActions.createQRTemplateFailure({
              error: error?.message ?? 'Failed to create QR template',
            }))
          )
        )
      )
    )
  );

  updateQRTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.updateQRTemplate),
      switchMap((action) =>
        this.qrTemplatesService.updateQRTemplate(action.uuid, action.payload).pipe(
          map((template) => QRTemplatesActions.updateQRTemplateSuccess({ template })),
          catchError((error) =>
            of(QRTemplatesActions.updateQRTemplateFailure({
              error: error?.message ?? 'Failed to update QR template',
            }))
          )
        )
      )
    )
  );

  deleteQRTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.deleteQRTemplate),
      switchMap((action) =>
        this.qrTemplatesService.deleteQRTemplate(action.uuid).pipe(
          map(() => QRTemplatesActions.deleteQRTemplateSuccess({ uuid: action.uuid })),
          catchError((error) =>
            of(QRTemplatesActions.deleteQRTemplateFailure({
              error: error?.message ?? 'Failed to delete QR template',
            }))
          )
        )
      )
    )
  );

  deleteQRTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QRTemplatesActions.deleteQRTemplateSuccess),
      withLatestFrom(this.store.select(QRTemplatesSelectors.selectQRTemplatesLastLoadParams)),
      map(([, { page, perPage, search, sort, sortDirection }]) =>
        QRTemplatesActions.loadQRTemplates({ page, perPage, search, sort, sortDirection })
      )
    )
  );
}
